customElements.define('x-accordion', class Accordion extends HTMLElement {
  #template = `
    <style>
      .root {
        transition: height 300ms;
        overflow: hidden;
      }

      :host(:not([open])) .root {
        height: 0;
      }

      :host([open]) .root {
        height: var(--content-height, 0);
      }

      :host {
        display: flex;
        flex-direction: column;
        gap: 15px;
      }

      :host([open]) slot[name="open"] {
        display: none;
      }

      :host(:not([open])) slot[name="close"] {
        display: none;
      }
    </style>
    <slot name="open"></slot>
    <slot name="close"></slot>
    <div class="root" inert>
      <slot></slot>
    </div>
  `;

  constructor() {
    super();

    const shadow = this.attachShadow({mode: 'open'});
    shadow.innerHTML = this.#template;

    this.root = shadow.querySelector('.root');
    this.abortController = new AbortController();
  }

  get open() {
    return this.hasAttribute('open');
  }

  get duration() {
    return +(this.getAttribute('duration') ?? '0');
  }

  set duration(d) {
    this.setAttribute('duration', d);
  }

  set open(open) {
    if (open) {
      this.setAttribute('open', '');
      this.root.removeAttribute('inert');
    } else {
      this.root.setAttribute('inert', '');
      this.removeAttribute('open');
    }
  }

  #getSlot(name) {
    return name
      ? this.shadowRoot.querySelector(`slot[name="${name}"]`)
      : this.shadowRoot.querySelector(`slot:not([name])`);
  }

  disconnectedCallback() {
    this.abortController.abort('disconnected');
  }

  connectedCallback() {
    const openSlot = this.#getSlot('open');
    const closeSlot = this.#getSlot('close');
    const defaultSlot = this.#getSlot();
    const contentEl = defaultSlot.parentElement;

    // Setting up a listener to toggle the accordion
    openSlot.addEventListener('click', () => {
      this.open = true;
    }, {signal: this.abortController.signal});
    closeSlot.addEventListener('click', () => {
      this.open = false;
    }, {signal: this.abortController.signal});

    // Setting up a mutation observer to get the true height of the default slot
    let lastHeight = 0;
    const adjustContentHeight = () => {
      if (contentEl.scrollHeight === lastHeight) {
        return;
      }
      
      lastHeight = contentEl.scrollHeight;
      this.style.setProperty('--content-height', `${contentEl.scrollHeight}px`);
    };
    let frameId = requestAnimationFrame(function check () {
      adjustContentHeight();
      frameId = requestAnimationFrame(check);
    });
    this.abortController.signal.addEventListener('abort', () => void window.cancelAnimationFrame(frameId));
    adjustContentHeight();
  }
});